/**
 * FlahaSOIL v2 — Project detail (Phase 8A).
 *
 * Shows the project header, a list of its soil samples and, on
 * expansion, the soil tests captured for each sample. Quick actions
 * link to the wizard preselected with `?projectId=<id>` and to a
 * sample-scoped wizard call when the user wants to add another test.
 */
import {
	Alert,
	Box,
	Button,
	Chip,
	Divider,
	Skeleton,
	Stack,
	Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DescriptionIcon from "@mui/icons-material/Description";
import ScienceIcon from "@mui/icons-material/Science";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import type { GetProjectResponse } from "@flaha/shared-types";

import { ProjectSampleRow } from "../features/projects/components/ProjectSampleRow";
import { GlassSurface } from "../layouts/components/GlassSurface";
import { usePageHeader } from "../layouts/PageHeaderContext";
import { getApiClient } from "../services/apiClientProvider";
import { useAuth } from "../auth";

export function ProjectDetailPage() {
	const { projectId = "" } = useParams<{ projectId: string }>();
	const navigate = useNavigate();
	const { status: sessionStatus, activeOrganization } = useAuth();
	const [data, setData] = useState<GetProjectResponse | null>(null);
	const [error, setError] = useState<string | null>(null);
	const requestId = useRef(0);

	usePageHeader({
		title: data?.project.name ?? "Project",
		subtitle: data
			? `${data.project.status} · ${data.samples.length} sample${data.samples.length === 1 ? "" : "s"}`
			: "Loading project…",
		breadcrumbs: [
			{ label: "Home", to: "/" },
			{ label: "Dashboard", to: "/dashboard" },
			{ label: "Projects", to: "/projects" },
			{ label: data?.project.name ?? "…" },
		],
		...(data
			? {
					projectContext: {
						id: data.project.id,
						name: data.project.name,
						...(data.project.code ? { code: data.project.code } : {}),
					},
				}
			: {}),
	});

	const load = useCallback(() => {
		const currentRequest = ++requestId.current;
		setError(null);
		setData(null);
		getApiClient()
			.getProjectById(projectId)
			.then((response) => {
				if (requestId.current === currentRequest) setData(response);
			})
			.catch((err: unknown) => {
				if (requestId.current === currentRequest) {
					setError(err instanceof Error ? err.message : String(err));
				}
			});
	}, [projectId]);

	useEffect(() => {
		// Defer until auth is resolved; the realApiV2Client attaches the
		// JWT from accessTokenStore and AuthProvider populates it on hydrate.
		if (sessionStatus !== "authenticated") return;
		load();
	}, [load, sessionStatus, activeOrganization?.id]);

	if (error) {
		return (
			<Alert
				severity="error"
				action={
					<Button color="inherit" size="small" onClick={load}>
						Retry
					</Button>
				}
			>
				Failed to load project {projectId}: {error}
			</Alert>
		);
	}
	if (!data) {
		return (
			<GlassSurface aria-label="Loading project" aria-busy="true" sx={{ p: 3 }}>
				<Skeleton width="45%" height={38} />
				<Skeleton width="72%" />
				<Skeleton height={96} />
			</GlassSurface>
		);
	}

	const { project, samples } = data;

	return (
		<Box data-testid="project-detail-workspace" sx={{ minWidth: 0 }}>
			<GlassSurface
				component="section"
				aria-labelledby="project-overview-title"
				intensity="strong"
				depth="floating"
				sx={{ p: { xs: 2.25, sm: 3 }, mb: 3 }}
			>
			<Stack
				direction={{ xs: "column", sm: "row" }}
				justifyContent="space-between"
				alignItems={{ xs: "flex-start", sm: "flex-start" }}
				spacing={2}
				sx={{ mb: 3 }}
			>
				<Box>
					<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
						<Typography id="project-overview-title" component="h1" variant="h4">
							{project.name}
						</Typography>
						<Chip label={project.status} size="small" color={project.status === "ACTIVE" ? "success" : "default"} />
					</Stack>
					<Typography variant="body2" color="text.secondary">
						{project.locationName ?? "No location set"}
					</Typography>
					{project.description && (
						<Typography sx={{ mt: 1 }}>{project.description}</Typography>
					)}
				</Box>
				<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
					<Button
						component={RouterLink}
						to="/projects"
						variant="outlined"
					>
						All projects
					</Button>
					<Button
						component={RouterLink}
						to={`/projects/${project.id}/reports`}
						variant="outlined"
						startIcon={<DescriptionIcon />}
					>
						Reports
					</Button>
					<Button
						variant="contained"
						startIcon={<ScienceIcon />}
						onClick={() =>
							navigate(`/soil-tests/new?projectId=${project.id}`)
						}
					>
						New soil test
					</Button>
				</Stack>
			</Stack>

				<Divider sx={{ mb: 2 }} />
				<Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1.5, sm: 4 }}>
						<SummaryStat label="Samples" value={samples.length} />
						<SummaryStat
							label="Created"
							value={new Date(project.createdAt).toLocaleDateString()}
						/>
						<SummaryStat
							label="Last updated"
							value={new Date(project.updatedAt).toLocaleDateString()}
						/>
				</Stack>
			</GlassSurface>

			<Typography component="h2" variant="h5" sx={{ mb: 1 }}>
				Soil samples
			</Typography>
			<Typography color="text.secondary" sx={{ mb: 2 }}>
				Expand a sample to load its soil tests, analysis results, and report routes.
			</Typography>
			<Divider sx={{ mb: 2 }} />

			{samples.length === 0 ? (
				<GlassSurface component="section" aria-label="No soil samples" sx={{ p: 4, textAlign: "center" }}>
					<Typography color="text.secondary" sx={{ mb: 2 }}>
						No samples yet. Soil samples are created when you start a soil
						test from this project.
					</Typography>
					<Button
						variant="contained"
						startIcon={<AddIcon />}
						onClick={() =>
							navigate(`/soil-tests/new?projectId=${project.id}`)
						}
					>
						Start first soil test
					</Button>
				</GlassSurface>
			) : (
				<Stack spacing={1}>
					{samples.map((s) => (
						<ProjectSampleRow key={s.id} sample={s} projectId={project.id} />
					))}
				</Stack>
			)}
		</Box>
	);
}

function SummaryStat({ label, value }: { label: string; value: string | number }) {
	return (
		<Box>
			<Typography variant="caption" color="text.secondary">
				{label}
			</Typography>
			<Typography variant="h6">{value}</Typography>
		</Box>
	);
}
